import React, { useMemo } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Select,
  MenuItem,
  TextField,
  IconButton,
  Button,
  FormControl,
  Divider,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

const FORMAS_PAGO = [
  { codigo: "20", nombre: "Otros con Utilización del Sistema Financiero" },
  { codigo: "01", nombre: "Sin utilización del Sistema Financiero" },
  { codigo: "15", nombre: "Compensación de Deudas" },
  { codigo: "16", nombre: "Tarjeta de débito" },
  { codigo: "17", nombre: "Dinero Electrónico" },
  { codigo: "18", nombre: "Tarjeta Prepago" },
  { codigo: "19", nombre: "Tarjeta de crédito" },
  { codigo: "21", nombre: "Endoso de títulos" },
];

const money2 = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return "0.00";
  return x.toFixed(2);
};

const disabledFieldSx = {
  "& .Mui-disabled": {
    backgroundColor: "#f0f0f0 !important",
    color: "#333 !important",
    WebkitTextFillColor: "#333 !important",
  },
};

const pickPagos = (doc) => {
  const fp = doc?.formas_pago;
  return Array.isArray(fp) ? fp : [];
};

const pickTotal = (doc) => {
  const t = doc?.totales && typeof doc.totales === "object" ? doc.totales : null;
  const total = t?.total ?? doc?.total ?? 0;
  return money2(total);
};

const normalizeRow = (p) => {
  return {
    forma_pago: String(p.forma_pago ?? p.codigo ?? "20"),
    plazo: String(p.plazo ?? 0),
    unidad_tiempo: String(p.unidad_tiempo ?? "dias"),
    valor: money2(p.total ?? p.valor ?? p.monto ?? 0),
  };
};

export default function InvoicePayments({ doc }) {
  const pagos = useMemo(() => pickPagos(doc), [doc]);
  const rows = useMemo(() => pagos.map(normalizeRow), [pagos]);
  const totalPagar = useMemo(() => pickTotal(doc), [doc]);

  const totalAsignado = useMemo(() => {
    return rows.reduce((acc, r) => acc + (Number(r.valor) || 0), 0);
  }, [rows]);

  const isMatch = money2(totalPagar) === money2(totalAsignado);

  return (
    <Box>
      <Paper sx={{ borderRadius: 2, border: "1px solid #e6e6e6" }}>
        <Box sx={{ bgcolor: "#f3f3f3", px: 2, py: 1.25 }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={5}>
              <Typography fontWeight={700}>Forma de pago</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Plazo</Typography>
            </Grid>
            <Grid item xs={12} md={2.5}>
              <Typography fontWeight={700}>Unid. de tiempo</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography fontWeight={700}>Valor</Typography>
            </Grid>
            <Grid item xs={12} md={0.5} />
          </Grid>
        </Box>

        <Box sx={{ p: 2 }}>
          {rows.length ? (
            rows.map((r, idx) => (
              <Grid
                container
                spacing={1.5}
                key={idx}
                alignItems="center"
                sx={{ mb: idx === rows.length - 1 ? 0 : 2 }}
              >
                <Grid item xs={12} md={5}>
                  <FormControl fullWidth>
                    <Select value={r.forma_pago} disabled>
                      {FORMAS_PAGO.map((f) => (
                        <MenuItem key={f.codigo} value={f.codigo}>
                          {f.nombre}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField value={r.plazo} fullWidth disabled sx={disabledFieldSx} />
                </Grid>

                <Grid item xs={12} md={2.5}>
                  <FormControl fullWidth>
                    <Select value={r.unidad_tiempo} disabled>
                      <MenuItem value="dias">días</MenuItem>
                      <MenuItem value="meses">meses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={2}>
                  <TextField value={r.valor} fullWidth disabled sx={disabledFieldSx} />
                </Grid>

                <Grid item xs={12} md={0.5} sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <IconButton disabled>
                    <DeleteOutlineIcon />
                  </IconButton>
                </Grid>
              </Grid>
            ))
          ) : (
            <Typography sx={{ color: "#666" }}>Sin formas de pago registradas.</Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<AddCircleOutlineIcon />} disabled>
              Agregar detalle
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 3 }}>
          <Box textAlign="right">
            <Typography fontWeight={700}>Total a pagar</Typography>
            <Typography>${money2(totalPagar)}</Typography>
          </Box>

          <Box textAlign="right">
            <Typography fontWeight={700}>Total asignado</Typography>
            <Typography sx={{ color: isMatch ? "#2e7d32" : "#b71c1c" }}>
              ${money2(totalAsignado)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
